import * as React from 'react';
import { useRef } from 'react';
import { assignProps, createViewConfig, view, toIntent, scheduleStyleIntent } from '@sail/engine';
import { useId, useFocusVisible, mergeRefs } from '@sail/react-aria';
import { tokens as $ } from '@sail/tokens';
import { useFloating } from './popover/useFloating';
import { css } from '../css';
import { Layer } from './layers/Layer';
import { toFloatingPlacement } from './utils/toFloatingPlacement';
import { useTooltipIframe } from '../iframe-overrides/TooltipOverrides';
import { useUiConfig } from '../providers/UiConfigProvider';
const DEFAULT_OPEN_DELAY = 250;
const DEFAULT_CLOSE_DELAY = 500;
const MINIMUM_DURATION = 800;
const tooltipLayerIframeOverrides = toIntent([
    assignProps(useTooltipIframe),
    // We "hijack" the overlayRef prop on the TooltipLayer as we want to
    // assign the overlayRef to the iframe that we created
    assignProps({
        overlayRef: {
            current: null
        }
    })
]);
const triggerIsolationClassName = 'tooltip-trigger-isolate';
scheduleStyleIntent(css.global({
    [`.${triggerIsolationClassName}`]: {
        isolation: 'isolate'
    }
}));
function tooltipStateReducer(state, action) {
    switch(action){
        case 'open':
            return {
                status: 'open',
                pending: null,
                openedAt: Date.now()
            };
        case 'close':
            return {
                ...state,
                status: 'closed',
                pending: null,
                openedAt: null
            };
        default:
            {
                return {
                    ...state,
                    pending: action
                };
            }
    }
}
const getOpenDelay = (delay)=>{
    if (typeof delay === 'number') {
        return delay;
    }
    var _delay_show;
    return (_delay_show = delay === null || delay === void 0 ? void 0 : delay.show) !== null && _delay_show !== void 0 ? _delay_show : DEFAULT_OPEN_DELAY;
};
const getCloseDelay = (delay)=>{
    if (typeof delay === 'number') {
        return DEFAULT_CLOSE_DELAY;
    }
    var _delay_hide;
    return (_delay_hide = delay === null || delay === void 0 ? void 0 : delay.hide) !== null && _delay_hide !== void 0 ? _delay_hide : DEFAULT_CLOSE_DELAY;
};
// animation.duration.long token is 300ms + 50ms of buffer
const TRANSITION_TIMEOUT = 350;
const useTooltipAnimation = (isOpen)=>{
    const [visible, setVisible] = React.useState(false);
    const uiConfig = useUiConfig();
    const shouldApplyIframeOverrides = !!(uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig._unstable_requestIframe);
    const transitionTimeoutRef = React.useRef(null);
    React.useEffect(()=>{
        if (isOpen && !visible) {
            setVisible(true);
        } else if (!isOpen && visible) {
            transitionTimeoutRef.current = window.setTimeout(()=>{
                setVisible(false);
                transitionTimeoutRef.current = null;
            }, TRANSITION_TIMEOUT);
            return ()=>{
                if (transitionTimeoutRef.current !== null) {
                    window.clearTimeout(transitionTimeoutRef.current);
                    transitionTimeoutRef.current = null;
                }
            };
        }
    }, [
        isOpen,
        visible
    ]);
    if (process.env.NODE_ENV === 'test') {
        return {
            shouldRender: isOpen,
            animate: css({
                opacity: 1
            })
        };
    }
    // we need 0.999 to trigger onTransitionEnd in the iframe overrides
    const baseOpacity = shouldApplyIframeOverrides ? 0.999 : 0;
    return {
        shouldRender: isOpen || visible,
        animate: toIntent([
            css({
                opacity: baseOpacity,
                transitionProperty: 'opacity',
                transitionDuration: $['animation.duration.long'],
                transitionTimingFunction: $['animation.easing'],
                '@media (prefers-reduced-motion: reduce)': {
                    transitionDuration: 'instant',
                    transitionTimingFunction: 'instant'
                }
            }),
            visible && isOpen ? css({
                opacity: 1,
                transitionDuration: $['animation.duration.short']
            }) : css({
                opacity: baseOpacity,
                transitionDuration: $['animation.duration.long']
            }),
            assignProps({
                onTransitionEnd: ()=>{
                    if (!isOpen) {
                        if (transitionTimeoutRef.current !== null) {
                            window.clearTimeout(transitionTimeoutRef.current);
                            transitionTimeoutRef.current = null;
                        }
                        setVisible(false);
                    }
                }
            })
        ])
    };
};
const tooltips = {};
const tooltipRegistry = {
    register (id, closeCallback) {
        tooltips[id] = closeCallback;
    },
    unregister (id) {
        delete tooltips[id];
    },
    closeOtherTooltips (exceptId) {
        Object.keys(tooltips).forEach((id)=>{
            if (id !== exceptId) {
                tooltips[id]();
            }
        });
    },
    hasActiveTooltips () {
        return Object.keys(tooltips).length > 0;
    }
};
function useTooltipState({ id, delay }) {
    const { isFocusVisible } = useFocusVisible();
    const [state, dispatch] = React.useReducer(tooltipStateReducer, {
        status: 'closed',
        pending: null,
        openedAt: null
    });
    const openDelay = getOpenDelay(delay);
    const closeDelay = getCloseDelay(delay);
    const open = React.useCallback(({ immediate = false } = {})=>{
        dispatch(immediate || openDelay <= 0 || tooltipRegistry.hasActiveTooltips() ? 'open' : {
            command: 'open',
            after: 'warmup'
        });
    }, [
        openDelay
    ]);
    const close = React.useCallback(({ immediate = false } = {})=>{
        if (isFocusVisible) {
            return dispatch('close');
        }
        dispatch({
            command: 'close',
            after: immediate ? 'minimum' : 'cooldown'
        });
    }, [
        isFocusVisible
    ]);
    React.useEffect(()=>{
        if (state.status === 'open') {
            tooltipRegistry.closeOtherTooltips(id);
            tooltipRegistry.register(id, ()=>dispatch('close'));
        }
        if (state.status === 'closed') {
            tooltipRegistry.unregister(id);
        }
    }, [
        id,
        state.status
    ]);
    React.useEffect(()=>{
        var _state_pending, _state_pending1;
        if (((_state_pending = state.pending) === null || _state_pending === void 0 ? void 0 : _state_pending.command) === 'open') {
            const timeout = window.setTimeout(()=>{
                dispatch('open');
            }, openDelay);
            return ()=>{
                window.clearTimeout(timeout);
            };
        }
        if (((_state_pending1 = state.pending) === null || _state_pending1 === void 0 ? void 0 : _state_pending1.command) === 'close') {
            const after = state.pending.after;
            const uptime = state.openedAt ? Date.now() - state.openedAt : 0;
            const closeAt = after === 'minimum' ? MINIMUM_DURATION - uptime : closeDelay;
            if (closeAt <= 0) {
                dispatch('close');
            } else {
                const timeout = window.setTimeout(()=>{
                    dispatch('close');
                }, closeAt);
                return ()=>{
                    window.clearTimeout(timeout);
                };
            }
        }
    }, [
        openDelay,
        closeDelay,
        state,
        isFocusVisible
    ]);
    return {
        isOpen: state.status === 'open',
        open,
        close
    };
}
// This config is used by submerchant-surfaces IframeLayerOverrides enabling them
// to switch out the contents of the positioned tooltip with iframed content
// and providing them access to the reference and overlay elements.
export const TooltipLayerConfig = createViewConfig({
    name: 'TooltipLayer',
    props: {}
});
const TooltipLayer = TooltipLayerConfig.createView(({ id, placement, floatingStrategy, type, overlayRef, triggerRef, children, subviews, fallbackPlacements, ...props })=>{
    var _middlewareData_hide;
    const padding = 4;
    const floatingPlacement = toFloatingPlacement(placement);
    const fallbackFloatingPlacements = fallbackPlacements === null || fallbackPlacements === void 0 ? void 0 : fallbackPlacements.map((placement)=>toFloatingPlacement(placement));
    const { refs, floatingStyles, middlewareData } = useFloating({
        placement: floatingPlacement,
        floatingStrategy,
        offsetValue: padding,
        flipEnabled: true,
        fallbackPlacements: fallbackFloatingPlacements,
        hideOptions: {
            referenceHidden: true
        },
        shiftEnabled: true,
        shiftOptions: {
            padding
        }
    });
    const referenceHidden = (_middlewareData_hide = middlewareData.hide) === null || _middlewareData_hide === void 0 ? void 0 : _middlewareData_hide.referenceHidden;
    return /*#__PURE__*/ React.createElement(Layer, {
        inherits: subviews.layer,
        ref: (el)=>{
            // don't run positioning logic within JSDOM environment (speeds up tests)
            // JSDOM doesn't support `getBoundingClientRect` anyway, so positioning logic
            // wouldn't be correct there anyway.
            if (typeof jest === 'undefined') {
                refs.setFloating(el);
                refs.setReference(triggerRef.current);
            }
        },
        style: floatingStyles,
        uses: [
            assignProps({
                ref: overlayRef
            })
        ]
    }, /*#__PURE__*/ React.createElement(view.div, {
        ...props,
        id: id,
        role: "tooltip",
        // hide the tooltip if the reference element is not visible
        css: [
            referenceHidden ? css({
                visibility: 'hidden'
            }) : css({
                visibility: 'visible'
            })
        ]
    }, children));
});
export const TooltipConfig = createViewConfig({
    props: {},
    name: 'Tooltip',
    defaults: {
        type: 'description',
        placement: 'bottom'
    }
});
/**
 * @external
 */ export const Tooltip = TooltipConfig.createView(({ trigger, placement, floatingStrategy, type, delay = DEFAULT_OPEN_DELAY, children, fallbackPlacements, ...props })=>{
    if (/*#__PURE__*/ React.isValidElement(trigger) && trigger.type === Symbol.for('react.fragment') && process.env.NODE_ENV !== 'production') {
        throw new Error('React fragments are not supported as Sail Tooltip triggers.');
    }
    const id = useId();
    const { isFocusVisible } = useFocusVisible();
    const triggerRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    const uiConfig = useUiConfig();
    const shouldApplyIframeOverrides = !!(uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig._unstable_requestIframe);
    React.useEffect(()=>{
        if ('current' in trigger) {
            triggerRef.current = trigger.current;
            if (triggerRef.current) {
                // ensure the tooltip trigger creates a new stacking context
                // this enables the tooltip trigger to be compatible with Sail's `List` component
                triggerRef.current.classList.add(triggerIsolationClassName);
            }
        }
    }, [
        trigger
    ]);
    const { isOpen, open: showTooltip, close: hideTooltip } = useTooltipState({
        id,
        delay
    });
    const { shouldRender, animate } = useTooltipAnimation(isOpen);
    const position = useRef({
        clientX: 0,
        clientY: 0
    });
    const closeDelay = getCloseDelay(delay);
    React.useEffect(()=>{
        const triggerElement = triggerRef.current;
        const overlayElement = overlayRef.current;
        function onFocus() {
            if (isFocusVisible) {
                showTooltip({
                    immediate: true
                });
            }
        }
        function onBlur() {
            hideTooltip({
                immediate: true
            });
        }
        function onMouseEnter(e) {
            position.current.clientX = e.clientX;
            position.current.clientY = e.clientY;
            showTooltip();
        }
        function onMouseLeave(e) {
            if (// if mouse has remained in the same position since opening, then stay open
            position.current.clientX !== e.clientX || position.current.clientY !== e.clientY || // if mouse has left the window, then close
            e.clientY === 0 && e.clientX === 0) {
                hideTooltip();
            }
        }
        if (triggerElement) {
            triggerElement.addEventListener('focus', onFocus);
            triggerElement.addEventListener('blur', onBlur);
            triggerElement.addEventListener('mouseenter', onMouseEnter);
            triggerElement.addEventListener('mouseleave', onMouseLeave);
        }
        if (overlayElement) {
            if (closeDelay > 0) {
                overlayElement.addEventListener('focus', onFocus);
                overlayElement.addEventListener('blur', onBlur);
                overlayElement.addEventListener('mouseenter', onMouseEnter);
            }
            // in the edge case where the overlay intersects the pointer when it opens,
            // we need to make sure that the tooltip closes when the pointer leaves the overlay
            overlayElement.addEventListener('mouseleave', onMouseLeave);
        }
        if (isOpen) {
            document.body.addEventListener('scroll', onBlur, {
                capture: true
            });
        }
        return ()=>{
            if (triggerElement) {
                triggerElement.removeEventListener('focus', onFocus);
                triggerElement.removeEventListener('blur', onBlur);
                triggerElement.removeEventListener('mouseenter', onMouseEnter);
                triggerElement.removeEventListener('mouseleave', onMouseLeave);
            }
            if (overlayElement) {
                if (type === 'description') {
                    overlayElement.removeEventListener('focus', onFocus);
                    overlayElement.removeEventListener('blur', onBlur);
                    overlayElement.removeEventListener('mouseenter', onMouseEnter);
                }
                overlayElement.removeEventListener('mouseleave', onMouseLeave);
            }
            if (isOpen) {
                document.body.removeEventListener('scroll', onBlur, {
                    capture: true
                });
            }
        };
    }, [
        triggerRef,
        overlayRef,
        showTooltip,
        hideTooltip,
        isOpen,
        type,
        isFocusVisible,
        closeDelay
    ]);
    React.useEffect(()=>{
        function onKeyDown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                hideTooltip({
                    immediate: true
                });
            }
        }
        if (isOpen) {
            window.addEventListener('keydown', onKeyDown);
            return ()=>{
                window.removeEventListener('keydown', onKeyDown);
            };
        }
    }, [
        isOpen,
        hideTooltip
    ]);
    React.useEffect(()=>{
        const triggerElement = triggerRef.current;
        if (!(triggerElement === null || triggerElement === void 0 ? void 0 : triggerElement.getAttribute('tabindex'))) {
            triggerElement === null || triggerElement === void 0 ? void 0 : triggerElement.setAttribute('tabindex', '0');
        }
        if (isOpen) {
            const attribute = type === 'description' ? 'aria-describedby' : 'aria-labelledby';
            triggerElement === null || triggerElement === void 0 ? void 0 : triggerElement.setAttribute(attribute, id);
            return ()=>{
                triggerElement === null || triggerElement === void 0 ? void 0 : triggerElement.removeAttribute(attribute);
            };
        }
    }, [
        isOpen,
        id,
        type
    ]);
    var _trigger_props_className;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.isValidElement(trigger) && /*#__PURE__*/ React.cloneElement(trigger, {
        ref: mergeRefs(// eslint-disable-next-line @typescript-eslint/no-explicit-any
        trigger.ref, triggerRef),
        className: `${(_trigger_props_className = trigger.props.className) !== null && _trigger_props_className !== void 0 ? _trigger_props_className : ''} ${triggerIsolationClassName}`.trim()
    }), shouldRender && React.Children.count(children) > 0 ? /*#__PURE__*/ React.createElement(TooltipLayer, {
        ...props,
        id: id,
        type: type,
        floatingStrategy: floatingStrategy,
        placement: placement,
        fallbackPlacements: fallbackPlacements,
        overlayRef: overlayRef,
        triggerRef: triggerRef,
        uses: [
            ...shouldApplyIframeOverrides ? tooltipLayerIframeOverrides : [],
            animate
        ]
    }, children) : null);
}, {
    css: {
        font: 'body.small',
        boxShadow: 'small'
    },
    forward: {
        type: true
    },
    variants: {
        type: {
            description: {
                css: {
                    padding: 'medium',
                    maxWidth: '300px',
                    backgroundColor: 'surface',
                    textColor: 'text',
                    border: '1px solid',
                    borderColor: 'border',
                    borderRadius: 'medium'
                }
            },
            label: {
                css: {
                    paddingX: 'small',
                    paddingY: 'xsmall',
                    maxWidth: '200px',
                    textColor: $['color.neutral.0'],
                    backgroundColor: $['color.neutral.700'],
                    borderRadius: 'small'
                }
            }
        }
    }
});

//# sourceMappingURL=Tooltip.js.map